import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import {
  DEF, NICE, PALETTE, SHAPES, frameDefaults, imageDefaults, lightDefaults, overshoot, recessDefaults,
  shapeDefaults, textDefaults, textareaDefaults, videoDefaults
} from '../core/doc.js';
import { combineAxes } from '../core/anim.js';
import { b64ToBytes, boundsOf, extractMeshes, parseGLB, resolveBuffers } from '../core/glb.js';
import { buildMaskGeometry, panelGeometry } from '../core/mask.js';
import { content } from '../scene/context.js';
import { buildBitmapEffects, buildGeometry, buildTextEffects, buildTextGeometry, recessBorders } from '../scene/geometry.js';
import { objQuat, setHover } from '../scene/gizmo.js';
import { cameraHelperPositions, localBounds } from '../scene/helpers.js';
import { buildTextAreaTexture, eraseOf, paintTextArea, revealOf, textAreaStyles } from '../scene/textarea.js';
import { applyMap, getTexture } from '../scene/textures.js';
import { getVideoTexture } from '../scene/video.js';
import { emit, requestRender } from '../state/bus.js';
import { amendHistory, pushHistory } from '../state/history.js';
import { $, S, clamp } from '../state/store.js';
import { renderRails, syncGizmoBar, updateHint } from '../ui/rails.js';
import { toast } from '../ui/toast.js';
import { ensureFont, fontReady } from './fonts.js';

/* BLEND MODES — the ones a WebGL blend equation can actually produce without
   a custom shader. Three.js only ever combines source and destination as
   `srcFactor·src (equation) dstFactor·dst` — no per-channel nonlinearity, so
   Photoshop's Overlay/Soft Light/Hard Light/Color Dodge/Color Burn family is
   out of reach here; those need a compositing shader reading the frame so
   far, a materially bigger feature. What IS exactly reachable, not an
   approximation of it:

     Multiply  src·dst                 — DstColor, Zero
     Screen    src+dst−src·dst         — OneMinusDstColor, One  (the standard
                                          "screen via blend factors" identity)
     Add       src+dst, clamped        — THREE.AdditiveBlending
     Subtract  dst−src, clamped        — THREE.SubtractiveBlending
     Darken    min(src,dst) per channel — MinEquation, One/One
     Lighten   max(src,dst) per channel — MaxEquation, One/One

   Normal (THREE's default) needs nothing set at all. Applied here, not
   spread across every builder, because every object type funnels through
   this one function for its material. */
const BLEND_MODES = {
  multiply: { blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
              blendSrc: THREE.DstColorFactor, blendDst: THREE.ZeroFactor },
  screen:   { blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
              blendSrc: THREE.OneMinusDstColorFactor, blendDst: THREE.OneFactor },
  add:      { blending: THREE.AdditiveBlending },
  subtract: { blending: THREE.SubtractiveBlending },
  darken:   { blending: THREE.CustomBlending, blendEquation: THREE.MinEquation,
              blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor },
  lighten:  { blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
              blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor }
};
export const BLEND_NAMES = {
  normal: 'Normal', multiply: 'Multiply', screen: 'Screen', add: 'Add',
  subtract: 'Subtract', darken: 'Darken', lighten: 'Lighten'
};
/* A blended material has to draw in the transparent pass — sorted back-to-
   front, after whatever it blends with — regardless of its own opacity;
   Normal at opacity 1 is the only case with nothing behind it to blend
   against, so that one is left exactly as makeMaterial already built it.
   depthWrite off for the same reason every other transparent surface here
   turns it off: so this doesn't block something behind it that hasn't
   drawn yet in the same pass. */
function applyBlendMode(m, o){
  const mode = BLEND_MODES[o.blend];
  if(!mode) return;
  Object.assign(m, mode);
  m.transparent = true;
  m.depthWrite = false;
}

function makeMaterial(o){
  if(o.unlit){
    /* Flat colour — the equivalent of Blender's Emission shader. A genuinely
       unlit material, not a standard one with emissive turned up: no lighting,
       no environment reflection, no ambient occlusion, and tone mapping is off
       so the hex you pick is the hex that lands in the export. Casting a
       shadow still works (it's occlusion, not shading) and is controlled
       separately by o.shadow; receiving one is switched off where the meshes
       are built, since an unlit material would not visibly show it. */
    const m = new THREE.MeshBasicMaterial({
      color: new THREE.Color(o.color),
      transparent: o.opacity < 1,
      opacity: o.opacity,
      side: THREE.DoubleSide,
      toneMapped: false,
      fog: false
    });
    applyMap(o, m);
    applyBlendMode(m, o);
    return m;
  }
  const m = new THREE.MeshStandardMaterial({
    color:new THREE.Color(o.color),
    metalness:o.metalness, roughness:o.roughness,
    transparent:o.opacity<1, opacity:o.opacity,
    side:THREE.DoubleSide,
    envMapIntensity:S.envInt,
    vertexColors:o.type==='recess'
  });
  applyMap(o, m);
  applyBlendMode(m, o);
  return m;
}


/* Text is up to three layers, back to front: the shadow/glow bitmap, the outline
   shell, then the fill. Only the fill is always there.

   The outline is a second trace of the same copy with the silhouette grown
   outward, sitting a hair behind the fill — so the fill covers the middle and
   only the rim shows. Building it as a solid rather than a ring means no boolean
   geometry and no deciding which traced loops are counters. */
function buildText(o){
  const g = new THREE.Group();
  const P = o.params;
  const flat = P.mode === '2d';

  /* Glyphs are TRACED, so an unloaded face is not a cosmetic problem — canvas
     silently substitutes a fallback and we extrude the wrong letterforms. Build
     with whatever is available now and rebuild when the real face arrives, which
     is exactly what setObjectTexture already does for an image that has not
     decoded. Keeping it callback-shaped is what saves every syncNode call site
     from having to become async. */
  if(!fontReady(P.font, P.weight, P.text))
    ensureFont(P.font, P.weight, P.text, ()=>{ if(o.node === g) { syncNode(o); requestRender(); } });

  const geo = buildTextGeometry(P, {flat});
  if(!geo) return g;

  const mats = [], texs = [];

  /* Depth of the fill, used to place the layers behind it. In 2D there is none,
     so the separations are derived from cap height instead — they only have to
     beat depth-buffer precision, not be visible. */
  const depth = flat ? 0 : Math.max(.001, P.depth);
  const eps = Math.max(2e-4, (P.capH || .9) * 2e-3);

  // ── shadow + glow bitmap ────────────────────────────────────────────────
  const fx = buildTextEffects(P);
  if(fx){
    const tex = new THREE.CanvasTexture(fx.canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const fxMat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true,
      depthWrite: false,       // never occlude the type standing in front of it
      toneMapped: false,       // the colour you picked is the colour that exports
      side: THREE.DoubleSide
    });
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(fx.w, fx.h), fxMat);
    plane.position.z = -depth/2 - eps*2;
    /* Not part of the object as far as the rest of the app is concerned: the
       selection box should hug the type rather than the halo, and a click in the
       transparent corner of the plane should not select the text. */
    plane.userData.fx = true;
    plane.raycast = () => {};
    g.add(plane);
    mats.push(fxMat); texs.push(tex);
  }

  // ── outline shell ───────────────────────────────────────────────────────
  if(P.fxOutline && (+P.fxOutlineW || 0) > 0){
    const oGeo = buildTextGeometry(P, {flat, dilatePct: +P.fxOutlineW});
    if(oGeo){
      const oMat = makeMaterial(o);
      oMat.side = THREE.FrontSide;
      oMat.color.set(P.fxOutlineColor || '#000000');
      const oMesh = new THREE.Mesh(oGeo, oMat);
      oMesh.position.z = -eps;
      oMesh.castShadow = o.shadow;
      g.add(oMesh);
      mats.push(oMat);
    }
  }

  // ── fill ────────────────────────────────────────────────────────────────
  const mat = makeMaterial(o);
  mat.side = flat ? THREE.DoubleSide : THREE.FrontSide;   // a flat face has a back
  /* ExtrudeGeometry emits two groups: 0 = the front/back faces, 1 = the extruded
     side walls. Giving the sides their own material lets the depth read in a
     different colour/finish from the face  the classic look of type standing
     proud of the recess. ShapeGeometry has no second group, so 2D has no sides. */
  let fillMats = mat, sideMat = null;
  if(!flat && o.sideOn && geo.groups && geo.groups.length > 1){
    sideMat = makeMaterial(o);
    sideMat.side = THREE.FrontSide;
    sideMat.color.set(o.sideColor || '#101010');
    if(o.sideRough != null) sideMat.roughness = o.sideRough;
    fillMats = [mat, sideMat];
  }
  const mesh = new THREE.Mesh(geo, fillMats);
  mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow && !o.unlit;
  g.add(mesh);

  /* The fill's materials go last in the list but FIRST in userData.mats, because
     the inspector patches mats[0] in place for colour and roughness and means
     the fill by it. */
  g.userData.mats = (sideMat ? [mat, sideMat] : [mat]).concat(mats);
  g.userData.texs = texs;
  g.userData.info = {tris:geo.userData.tris, clampedBevel:geo.userData.clampedBevel};
  return g;
}

/* Text Area: a canvas texture on a plane, never traced — see scene/textarea.js
   for why. The readiness gate mirrors buildText's, but walks every run in
   every paragraph rather than one flat run list. */
function buildTextArea(o){
  const g = new THREE.Group();
  const P = o.params;

  for(const r of textAreaStyles(P.paragraphs))
    if(!fontReady(r.font, r.weight, r.text))
      ensureFont(r.font, r.weight, r.text, ()=>{ if(o.node === g) { syncNode(o); requestRender(); } });

  const fx = buildTextAreaTexture(o);
  if(!fx) return g;

  const tex = new THREE.CanvasTexture(fx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;

  /* o.color behaves as a multiply tint over the whole canvas, the same
     convention the image object already uses (labelled "Tint" there too) —
     not a new idea, just applied to a generated bitmap instead of an
     uploaded one. */
  const mat = makeMaterial(o);
  mat.map = tex;
  mat.transparent = true;
  /* The canvas is mostly transparent padding around the actual glyphs — without
     this, that transparent rectangle still writes depth, so it can hide
     whatever sits behind it as if it were a solid card. Same fix, same reason,
     as the shadow/glow plane in buildText: only the ink itself should occlude
     anything, so the block always reads as its content sitting on top of the
     surface, not as an opaque tile floating over it. */
  mat.depthWrite = false;
  mat.needsUpdate = true;

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(fx.w, fx.h), mat);
  mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow && !o.unlit;
  g.add(mesh);

  g.userData.mats = [mat];
  g.userData.texs = [tex];
  g.userData.info = { w: fx.w, h: fx.h };
  /* Kept so a word reveal can repaint this exact canvas instead of rebuilding
     the block. Safe to hold because everything the plan depends on — the text,
     the width, the type size, the effects — goes through syncNode, which
     replaces this group and this plan along with it. Only `reveal` changes
     underneath it, and a reveal cannot reflow anything: the words arrive where
     they were always going to be. */
  g.userData.textarea = { plan: fx.plan, canvas: fx.canvas, tex };
  return g;
}

/**
 * Repaint a Text Area at its current reveal.
 *
 * The third kind of sync, beside transform and look: the value lives in a
 * BITMAP rather than in a matrix or a material, so neither of the others can
 * carry it. Redraws into the canvas already on the GPU and flags it — no new
 * texture, no new geometry, nothing allocated per frame, which is what makes
 * this affordable at 30fps for the length of an export.
 */
export function applyReveal(o){
  const ta = o.node && o.node.userData.textarea;
  if(!ta) return;
  paintTextArea(ta.plan, o, ta.canvas, revealOf(o), eraseOf(o));
  ta.tex.needsUpdate = true;
}

/* The shadow/glow/outline plane for a picture. Identical in every respect to
   the one buildText hangs behind its letters — same material, same exclusion
   from the selection box and from shadow casting — so the only thing that
   differs between a haloed word and a haloed photograph is where the
   silhouette came from. Returns the mesh, already parented, or null. */
function addBitmapFx(o, g, source, wM, hM, mask){
  const fx = buildBitmapEffects(o.params, source, wM, hM, mask);
  if(!fx) return null;
  const tex = new THREE.CanvasTexture(fx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true,
    depthWrite: false,       // never occlude the picture standing in front of it
    toneMapped: false,       // the colour you picked is the colour that exports
    side: THREE.DoubleSide
  });
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(fx.w, fx.h), mat);
  /* Behind the picture by a hair. Small enough that no camera angle in the app
     can see the gap, large enough to beat depth-buffer precision. */
  plane.position.z = -Math.max(2e-4, hM * 2e-3);
  plane.userData.fx = true;                  // out of the bounds box and the shadow pass
  plane.raycast = () => {};                  // and a click in its transparent corner selects nothing
  g.add(plane);
  (g.userData.mats = g.userData.mats || []).push(mat);
  (g.userData.texs = g.userData.texs || []).push(tex);
  return plane;
}

function buildImage(o){
  const g = new THREE.Group();
  if(!o.params.src) return g;
  // Build the mesh FIRST. getTexture may fire its callback synchronously when the
  // image is already cached (after undo, reload, or a repeat of the same file), so
  // the callback must not close over a `mesh` that hasn't been declared yet.
  const mat = makeMaterial(o);
  mat.transparent = true; mat.alphaTest = o.params.cutoff;
  const placeholderAR = 0.6;
  /* Plane, or a polygon cut of it when a mask is set. The mask points are in
     normalised image space, so they survive aspect changes; geomFor rebuilds the
     right shape for whatever aspect the loaded texture turns out to have. */
  const m = o.mask;
  const masked = m && m.on && !o._maskEdit && m.points && m.points.length >= 3;
  const geomFor = ar => {
    const wpx = o.params.w, hpx = o.params.w/(ar||1);
    if(masked){
      const mg = buildMaskGeometry(m.points, wpx, hpx, !!m.invert);
      if(mg) return mg;
    }
    return new THREE.PlaneGeometry(wpx, hpx);
  };
  const mesh = new THREE.Mesh(geomFor(placeholderAR), mat);
  mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow && !o.unlit;
  g.add(mesh);
  g.userData.mats = [mat];        // addBitmapFx appends to this, so it must exist first

  let fxPlane = null;
  const reshape = ge => {
    mesh.geometry.dispose();
    mesh.geometry = geomFor(ge.aspect||1);
    /* Rebuilt here rather than above, because the halo has to be the shape of
       the picture and the picture's proportions are not known until it decodes.
       Skipping it on the placeholder aspect costs one frame of no halo on a
       cold image, which is the same frame the mesh spends at 0.6. */
    if(fxPlane){ g.remove(fxPlane); fxPlane.geometry.dispose(); fxPlane = null; }
    const ar = ge.aspect || 1;
    fxPlane = addBitmapFx(o, g, ge.tex && ge.tex.image, o.params.w, o.params.w/ar,
                          masked ? m : null);
    requestRender();
  };
  const e = getTexture(o.params.src, reshape);
  mat.map = e.tex; mat.needsUpdate = true;
  if(e.loaded && !e.error) reshape(e);   // cache hit: size correctly right away
  return g;
}

/* A clip on a panel.

   Deliberately close to buildImage, and deliberately not merged with it. The
   two agree on everything the SCENE cares about — a picture plane, sized off
   a width in metres and the source's aspect, maskable, tintable — and differ
   on everything the DECODER cares about. Merging them would put a branch on
   o.type inside every line of a function whose whole job is to be simple.

   Three real differences, all consequences of the source being a video:

   1. No placeholder aspect. An image guesses 0.6 and reshapes when the file
      decodes, because the only cost is a flicker. A clip's dimensions are
      cached in params at import time (see core/doc.js's DEF.video) so the
      panel is the right shape on the very first build, before a single byte
      is decoded — which matters because the timing function is a pure
      function of those numbers and would otherwise answer differently before
      and after the first frame arrives.

   2. The texture is owned by scene/video.js's registry, not by this node, and
      so is NOT put in userData.texs — disposing it on rebuild would tear down
      a decoder that the next rebuild immediately needs. Same reasoning that
      keeps buildImage's cache-shared texture out of userData.texs, one step
      further along.

   3. It can have real thickness. A clip on a flat plane reads as a decal
      printed on the back of the recess; extruded and bevelled it reads as a
      screen set into it, which is the illusion the whole app is for. The
      edge takes its own material through the same sideOn/sideColor path 3D
      Text uses, off ExtrudeGeometry's two groups. */
function buildVideo(o){
  const g = new THREE.Group();
  const P = o.params;
  if(!P.src && !P.blobId && !P.assetId) return g;

  /* The PICTURE is always flat-lit, whatever o.unlit says. A clip already
     carries its own exposure and grade baked into its pixels; letting the
     scene's lights fall on it would darken and tint footage that is meant to
     export exactly as it came in — and on a screen set into a recess, a lit
     video reads as a poster of a video rather than as a screen. o.unlit is
     left to mean the EDGE (below), which is the surface that genuinely is a
     physical material. */
  const face = makeMaterial({ ...o, unlit: true });
  const entry = getVideoTexture(o);
  if(entry){ face.map = entry.tex; face.needsUpdate = true; }
  /* A clip with a matte blends. Only when it has one — turning this on for
     every video would make each of them a transparent object for the sorter to
     worry about, and pay a blend, for a picture with nothing to see through.

     depthWrite off for the same reason 3D Text turns it off: the see-through
     part of the plane still writes depth otherwise, and then hides whatever is
     behind it — which on a video is most of the plane, and the thing behind it
     is usually the inset the clip was supposed to be sitting inside. */
  if(P.srcAlpha){ face.transparent = true; face.depthWrite = false; }

  /* Cached metadata first, the decoder's answer second, 16:9 only if neither
     has spoken yet — which is the one frame between adding an object and its
     container being parsed. */
  const ar = (P.srcW && P.srcH ? P.srcW / P.srcH : entry?.aspect) || 16/9;
  const w = P.w, h = P.w / ar;

  const m = o.mask;
  const masked = m && m.on && !o._maskEdit && m.points && m.points.length >= 3;
  const geo = panelGeometry({
    w, h,
    points: masked ? m.points : null,
    invert: !!(masked && m.invert),
    depth: P.depth, bevel: P.bevel
  });

  const extruded = P.depth > 1e-4;
  face.side = extruded ? THREE.FrontSide : THREE.DoubleSide;
  let mats = face, sideMat = null;
  /* An extruded panel ALWAYS gets a second material, with no opt-in. One
     material cannot be both a flat-lit picture and a lit edge, and the edge
     is the whole reason for having depth: it is what catches a highlight and
     makes the thing read as a panel standing in the recess rather than a
     decal printed on the back of it. Dark by default, like a real bezel. */
  if(extruded && geo.groups && geo.groups.length > 1){
    sideMat = makeMaterial(o);                // honours o.unlit — lit by default
    sideMat.side = THREE.FrontSide;
    sideMat.map = null;                       // the edge is a colour, not a frame
    sideMat.color.set(o.sideColor || '#101010');
    if(o.sideRough != null) sideMat.roughness = o.sideRough;
    mats = [face, sideMat];
  }

  const mesh = new THREE.Mesh(geo, mats);
  mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow && !o.unlit;
  g.add(mesh);
  g.userData.mats = sideMat ? [face, sideMat] : [face];

  /* No source handed over, so the halo is the PANEL's shape — the mask if it
     has one, the rectangle otherwise. Deliberately not the moving picture: a
     clip's alpha changes every frame, and rebuilding a blurred bitmap at frame
     rate would cost more than the effect is worth. What it means in practice is
     that a matted clip glows around its panel rather than around whatever is in
     it this instant, which the inspector says out loud. */
  addBitmapFx(o, g, null, w, h, masked ? m : null);
  return g;
}

/* Euler angles that point local -Z from `pos` at `target`. Matrix4.lookAt puts +Z
   away from the target, which is exactly the convention three uses for lights and
   cameras, so the group's -Z becomes the beam direction. */
export function aimEuler(pos, target){
  const d = new THREE.Vector3().subVectors(pos, target);
  if(d.lengthSq() < 1e-9) return {x:0,y:0,z:0};
  const up = Math.abs(d.clone().normalize().y) > .999 ? new THREE.Vector3(0,0,1) : new THREE.Vector3(0,1,0);
  const m = new THREE.Matrix4().lookAt(pos, target, up);
  const e = new THREE.Euler().setFromRotationMatrix(m, 'XYZ');
  return {x:e.x*180/Math.PI, y:e.y*180/Math.PI, z:e.z*180/Math.PI};
}

/* A visible, clickable body for the light. Drawn unlit and tone-mapping-exempt so
   it reads as an overlay, and hidden outside Orbit so it can never reach the
   deliverable — the light itself still contributes everywhere. */
function buildLightViz(o){
  const P = o.params;
  const s = Math.max(.02, Math.max(S.screen.w, S.screen.h) * .018);
  const g = new THREE.Group();
  const col = new THREE.Color(o.color);
  const bulbMat = new THREE.MeshBasicMaterial({color:col, toneMapped:false});
  g.add(new THREE.Mesh(new THREE.SphereGeometry(s, 18, 12), bulbMat));

  const pts = [];
  if(P.kind === 'spot'){
    const len = s*11, rad = Math.tan(clamp(P.angle,1,89)*Math.PI/180)*len;
    for(let i=0;i<4;i++){ const a = i/4*Math.PI*2;
      pts.push(0,0,0, Math.cos(a)*rad, Math.sin(a)*rad, -len); }
    const seg = 30;
    for(let i=0;i<seg;i++){ const a=i/seg*Math.PI*2, b=(i+1)/seg*Math.PI*2;
      pts.push(Math.cos(a)*rad, Math.sin(a)*rad, -len, Math.cos(b)*rad, Math.sin(b)*rad, -len); }
  }else if(P.kind === 'directional'){
    const len = s*9, rad = s*2.4;
    pts.push(0,0,0, 0,0,-len);
    for(let i=0;i<5;i++){ const a = i/5*Math.PI*2;
      pts.push(Math.cos(a)*rad, Math.sin(a)*rad, 0, Math.cos(a)*rad, Math.sin(a)*rad, -len); }
  }else if(P.kind === 'ambient'){
    // No single direction to show, so the indicator reads as omnidirectional
    // in every axis at once — three rings instead of point's one flat ring.
    const rad = s*3, seg = 24;
    const ring = (ax,ay,az,bx,by,bz)=>{
      for(let i=0;i<seg;i++){ const a=i/seg*Math.PI*2, b=(i+1)/seg*Math.PI*2;
        pts.push(Math.cos(a)*rad*ax + Math.sin(a)*rad*bx, Math.cos(a)*rad*ay + Math.sin(a)*rad*by, Math.cos(a)*rad*az + Math.sin(a)*rad*bz,
                  Math.cos(b)*rad*ax + Math.sin(b)*rad*bx, Math.cos(b)*rad*ay + Math.sin(b)*rad*by, Math.cos(b)*rad*az + Math.sin(b)*rad*bz); }
    };
    ring(1,0,0, 0,1,0); ring(1,0,0, 0,0,1); ring(0,1,0, 0,0,1);
  }else{
    for(let i=0;i<6;i++){ const a = i/6*Math.PI*2;
      pts.push(Math.cos(a)*s*1.5, Math.sin(a)*s*1.5, 0, Math.cos(a)*s*3.2, Math.sin(a)*s*3.2, 0); }
  }
  // Fat lines (three's own LineSegments2/LineMaterial addon) instead of a
  // plain LineSegments — native WebGL linewidth is ignored by ANGLE/most
  // GPU drivers, so a regular THREE.LineBasicMaterial always draws 1px
  // regardless of its own linewidth. See scene/helpers.js's selBoxMat for
  // the fuller explanation; resolution is kept in sync per frame by
  // scene/renderer.js's syncFatLineRes(), which reads it off userData.mats.
  const lg = new LineSegmentsGeometry();
  lg.setPositions(pts);
  const lineMat = new LineMaterial({color:col, linewidth:2, transparent:true, opacity:.5, toneMapped:false});
  const lineSegs = new LineSegments2(lg, lineMat);
  // Unlike the bulb (always visible, so an unselected light can still be
  // found and clicked), the direction indicator only shows for the
  // selected light — see scene/gizmo.js's updateLightVizSelection(). A
  // cone/rings outline on every light in the scene regardless of selection
  // was clutter, not a handle.
  lineSegs.userData.lightVizDirection = true;
  g.add(lineSegs);
  g.userData.mats = [bulbMat, lineMat];
  return g;
}

function buildLight(o){
  const P = o.params, g = new THREE.Group();
  const col = new THREE.Color(o.color);
  const power = Math.max(0, P.intensity);
  let L;
  if(P.kind === 'point')            L = new THREE.PointLight(col, power, Math.max(0,P.distance)||0, Math.max(0,P.decay));
  else if(P.kind === 'directional') L = new THREE.DirectionalLight(col, power);
  else if(P.kind === 'ambient')     L = new THREE.AmbientLight(col, power);
  else L = new THREE.SpotLight(col, power, Math.max(0,P.distance)||0,
                               clamp(P.angle,1,89)*Math.PI/180, clamp(P.penumbra,0,1), Math.max(0,P.decay));
  /* Spot and directional lights ship with position (0,1,0), not the origin. Left
     alone, that one-unit offset combines with the target at (0,0,-1) to tilt the
     beam 45 degrees off the group's -Z, so aiming silently misses. */
  L.position.set(0,0,0);
  L.castShadow = !!o.shadow;
  if(L.shadow){
    // a point light needs six cube faces, so halve its map to keep the cost sane
    const res = P.kind==='point' ? Math.max(512, S.shadowRes/2) : S.shadowRes;
    L.shadow.mapSize.set(res, res);
    L.shadow.radius = S.shadowSoft;
    L.shadow.bias = -0.0006;
    L.shadow.normalBias = 0.02;
  }
  g.add(L);
  // spot and directional aim at a target; parenting it here makes the group's
  // rotation the beam direction, so the rotate gizmo aims the light
  if(L.target){ L.target.position.set(0,0,-1); g.add(L.target); }

  const viz = buildLightViz(o);
  viz.userData.lightViz = true;
  g.add(viz);
  g.userData.light = L;
  g.userData.mats = viz.userData.mats;
  return g;
}

function buildCameraObj(o){
  const g = new THREE.Group();
  const col = new THREE.Color(o.color);
  const s = Math.max(.02, Math.max(S.screen.w, S.screen.h) * 0.028);
  const bodyGeo = new LineSegmentsGeometry();
  bodyGeo.setPositions(cameraHelperPositions());
  const lm = new LineMaterial({color:col, linewidth:2, toneMapped:false});
  const body = new LineSegments2(bodyGeo, lm);
  body.scale.setScalar(s);
  const pm = new THREE.MeshBasicMaterial({color:col, toneMapped:false});
  const pip = new THREE.Mesh(new THREE.SphereGeometry(0.12*s,14,10), pm);
  g.add(body, pip);
  g.userData.mats = [lm, pm];
  return g;
}
export function setCameraObjViz(on){
  markersShown = on;
  S.objects.forEach(ob=>{
    if(ob.type==='camera' && ob.node) ob.node.visible = on && ob.visible;
    if(ob.type==='null') applyVisibility(ob);
  });
}
/* On-site's "hide the outlines" (Scene Settings): the null markers only, for the
   length of the mockup pass. The next frame's prepDraw puts them back through
   setCameraObjViz, so the other panes of Split keep theirs. */
export function setNullMarkers(on){
  const was = markersShown;
  markersShown = on;
  S.objects.forEach(ob=>{ if(ob.type==='null') applyVisibility(ob); });
  markersShown = was;
}
/* Whether workspace markers draw right now — off for the length of an export
   (export/frame.js). Kept as state rather than only set once, because a null
   is animated: every frame of an export re-runs applyVisibility on it, and
   without this each one would switch its marker straight back on. */
let markersShown = true;

/* THE NULL'S MARKER. A dashed-looking square in its own XY plane and a short
   cross through the centre on all three axes, so which way it is turned reads
   at a glance. Sized off the screen like the camera's body, so it is neither a
   speck on a wall nor a billboard on a phone. The sphere is invisible and only
   there to be clicked: a line is a few pixels wide, which is no target. */
function buildNullObj(o){
  const g = new THREE.Group();
  const s = Math.max(.03, Math.max(S.screen.w, S.screen.h) * 0.045);
  const h = s / 2, c = s * .32;
  const geo = new LineSegmentsGeometry();
  geo.setPositions([
    -h,-h,0,  h,-h,0,   h,-h,0,  h, h,0,   h, h,0, -h, h,0,  -h, h,0, -h,-h,0,
    -c, 0,0,  c, 0,0,   0,-c,0,  0, c,0,   0, 0,-c, 0, 0, c
  ]);
  const lm = new LineMaterial({ color: new THREE.Color(o.color), linewidth: 2, toneMapped: false, depthTest: false, transparent: true });
  const lines = new LineSegments2(geo, lm);
  lines.renderOrder = 10;
  const hit = new THREE.Mesh(new THREE.SphereGeometry(h * .7, 10, 8), new THREE.MeshBasicMaterial({ visible: false }));
  g.add(lines, hit);
  g.userData.mats = [lm, hit.material];
  return g;
}

/* Turn imported GLB bytes into a THREE.Group. Geometry is built synchronously;
   embedded textures decode asynchronously (Blob URL -> Image), the same shape as
   buildImage. The importer bakes a recenter+fit transform into params, applied on
   an inner group so the gizmo still drives the object's own pos/rot/scale. */
function bytesToObjectURL(bytes, mime){
  const blob = new Blob([bytes], {type: mime || 'image/png'});
  return URL.createObjectURL(blob);
}
function glbTexture(spec, srgb, onReady){
  const url = bytesToObjectURL(spec.bytes, spec.mime);
  const tex = new THREE.Texture();
  if(srgb) tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.flipY = false;                       // glTF UVs are not flipped
  const img = new Image();
  img.onload = ()=>{ tex.image = img; tex.needsUpdate = true; URL.revokeObjectURL(url); onReady && onReady(); };
  img.onerror = ()=>{ URL.revokeObjectURL(url); };
  img.src = url;
  return tex;
}
function glbMaterial(spec, shadow, onReady){
  const c = spec.color || [1,1,1,1];
  const m = new THREE.MeshStandardMaterial({
    color: new THREE.Color(c[0], c[1], c[2]),
    metalness: spec.metalness, roughness: spec.roughness,
    emissive: new THREE.Color(spec.emissive[0], spec.emissive[1], spec.emissive[2]),
    side: spec.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    envMapIntensity: S.envInt
  });
  if(c[3] < 1 || spec.alphaMode==='BLEND'){ m.transparent = true; m.opacity = c[3]; }
  if(spec.alphaMode==='MASK'){ m.alphaTest = spec.alphaCutoff; }
  if(spec.baseColorTex) m.map          = glbTexture(spec.baseColorTex, true,  onReady);
  if(spec.mrTex){       m.metalnessMap = m.roughnessMap = glbTexture(spec.mrTex, false, onReady); }
  if(spec.normalTex)    m.normalMap    = glbTexture(spec.normalTex, false, onReady);
  if(spec.emissiveTex)  m.emissiveMap  = glbTexture(spec.emissiveTex, true,  onReady);
  return m;
}
/* A MODEL FROM THE CLOUD ARRIVES AS AN ADDRESS, NOT AS BYTES.

   A project opened from the cloud has its model swapped for a signed storage
   URL (core/doc.js hydrate, since the rest of the app never learned about asset
   ids). Everything below decodes base64, so handing it that URL threw "The
   string to be decoded is not correctly encoded" and the model never appeared —
   in every cloud project that held one, while the file itself sat safely in
   storage and the animation on it loaded fine.

   So the bytes are fetched first and put where a model added by hand keeps
   them, a data URL, and the node is rebuilt from that. The assetId stays, so a
   save still writes the id and not the bytes. The empty group returned in the
   meantime is the same thing a failed build returns; it is replaced the moment
   the file lands. */
const fetchingModels = new WeakSet();
const isRemoteSrc = s => typeof s === 'string' && /^(https?:|blob:)/i.test(s);
async function inlineModelSource(o){
  const P = o.params, url = P.src;
  if(fetchingModels.has(P)) return;
  fetchingModels.add(P);
  try{
    const res = await fetch(url);
    if(!res.ok) throw new Error('the file could not be downloaded (HTTP ' + res.status + ')');
    const blob = await res.blob();
    const dataUrl = await new Promise((ok, no) => {
      const r = new FileReader();
      r.onload = () => ok(r.result);
      r.onerror = () => no(new Error('the file could not be read'));
      r.readAsDataURL(blob);
    });
    /* Only if the object is still here and still wants this file: the project
       may have been closed or the model replaced while it downloaded. */
    if(S.objects.includes(o) && P.src === url){
      P.src = dataUrl;
      syncNode(o);
      requestRender();
    }
  }catch(err){
    console.error('GLB download failed:', err);
    toast('Could not load the model "' + o.name + '": ' + err.message, true);
  }finally{ fetchingModels.delete(P); }
}
function buildGLBNode(o){
  const g = new THREE.Group();
  const mats = [];
  const P = o.params;
  if(isRemoteSrc(P.src)){ inlineModelSource(o); return g; }
  try{
    const b64 = /base64,(.*)$/.exec(P.src); const bytes = b64ToBytes(b64 ? b64[1] : P.src);
    const { json, bin } = parseGLB(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset+bytes.byteLength));
    const buffers = resolveBuffers(json, bin);
    const meshes = extractMeshes(json, buffers);
    // inner group carries the baked recenter + uniform fit scale from import time
    const inner = new THREE.Group();
    const c = P.center || [0,0,0], fit = P.fit || 1;
    inner.scale.setScalar(fit);
    inner.position.set(-c[0]*fit, -c[1]*fit, -c[2]*fit);
    let tris = 0;
    for(const md of meshes){
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(md.positions, 3));
      if(md.normals) geo.setAttribute('normal', new THREE.BufferAttribute(md.normals, 3));
      if(md.uvs)     geo.setAttribute('uv',     new THREE.BufferAttribute(md.uvs, 2));
      if(md.indices) geo.setIndex(new THREE.BufferAttribute(md.indices, 1));
      if(!md.normals) geo.computeVertexNormals();
      const mat = glbMaterial(md.material, o.shadow, requestRender);
      mats.push(mat);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.applyMatrix4(new THREE.Matrix4().fromArray(md.matrix));
      mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow;
      inner.add(mesh);
      tris += md.tris;
    }
    g.add(inner);
    g.userData.mats = mats;
    g.userData.texs = mats.flatMap(m=>[m.map,m.metalnessMap,m.normalMap,m.emissiveMap].filter(Boolean));
    g.userData.info = {tris};
    if(!P.tris) P.tris = tris;
  }catch(err){
    console.error('GLB build failed:', err);
    toast('Could not read that model: ' + err.message, true);
  }
  return g;
}

function buildNode(o){
  if(o.type==='model')  return buildGLBNode(o);
  if(o.type==='camera') return buildCameraObj(o);
  if(o.type==='null')   return buildNullObj(o);
  if(o.type==='light') return buildLight(o);
  if(o.type==='text')  return buildText(o);
  if(o.type==='textarea') return buildTextArea(o);
  if(o.type==='image') return buildImage(o);
  if(o.type==='video') return buildVideo(o);
  const mat = makeMaterial(o);
  const geo = buildGeometry(o.type,o.params);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = o.shadow; mesh.receiveShadow = o.shadow && !o.unlit;
  const g = new THREE.Group(); g.add(mesh);
  g.userData.mats = [mat];
  if(geo.userData.tris) g.userData.info = {tris:geo.userData.tris};
  return g;
}

/* A SHADOW THAT FADES WITH THE THING CASTING IT.

   A shadow map is binary: a texel is either occluded or it is not, and
   material.opacity plays no part in the depth pass that fills it. So fading an
   object out used to leave its shadow at full strength — a shadow cast by
   nothing — and the only defence was to switch it off entirely once the object
   was effectively invisible. That cut still exists below and still has to: an
   object at zero should cast nothing at all. Between 1 and 0 there was nothing.

   The way to grade a binary map is to make the occlusion partial in COVERAGE
   rather than in strength: discard a proportion of the fragments in the depth
   pass, so that proportion of texels records no occluder. An ordered dither is
   what decides which — a fixed 4x4 threshold pattern, not noise, so the result
   is stable frame to frame and reads as a shadow thinning rather than one
   crawling. PCFShadowMap's own filtering (scene/context.js) then averages the
   pattern back into a smooth lightening. It is the same trick Blender calls
   dithered shadow transparency.

   INTERLEAVED GRADIENT NOISE rather than an ordered matrix. A 4x4 Bayer
   pattern is the textbook choice and it was wrong here: the dither is applied
   in the SHADOW MAP's own space, so its lattice lands at shadow-texel scale and
   PCF's small kernel averages within one cell rather than across several. The
   result was a shadow with a visible grid in it. IGN is high-frequency and
   effectively unstructured — it reads as fine grain, which the same filtering
   does smooth — while staying a pure function of position, so it is still
   stable frame to frame and still needs no texture, no dynamic indexing and no
   integer ops that WebGL1 GLSL would refuse. */
const DITHER_GLSL = `
float ign(vec2 c){
  /* WRAPPED FIRST. gl_FragCoord runs to the shadow map's full 2048, and the
     magic constants below multiply it into the hundreds — at mediump, which is
     all a fragment shader is promised, that leaves too few mantissa bits for
     fract() to return anything well spread, and the thresholds bunch up. The
     measured symptom was a shadow that barely thinned until the object was
     nearly gone: at 0.4 opacity it was still discarding almost nothing.
     128 keeps the arithmetic small and repeats far too coarsely to see. */
  vec2 p = mod(c, 128.0);
  return fract(52.9829189 * fract(0.06711056 * p.x + 0.00583715 * p.y));
}
`;
/* WHO CASTS, AND HOW MUCH — in one place, because two paths reach it.

   applyLook is the obvious one. The other is a REBUILD: changing the opacity of
   a text, image or video object re-runs its builder rather than patching it
   (ui/rail-right.js), and a freshly built mesh carries the stock depth material
   at full strength. So a shadow fade set here only through applyLook survived
   until the next rebuild and then silently reverted — the shadow stayed solid
   however far the object faded, and came right only when the playhead moved and
   the animation player called applyLook again. Reported exactly that way:
   keyframes worked, dragging the slider did not.

   Called from syncNode for the same reason stampIntrinsicOpacity is: it is the
   one point every rebuild passes through, so no builder has to remember. */
export function syncShadowCasting(o, node){
  if(!node || node.userData.light) return;
  const a = clamp(o.opacity == null ? 1 : o.opacity, 0, 1);
  const casts = !!o.shadow && a > .01;
  node.traverse(c => {
    if(!c.isMesh) return;
    c.castShadow = casts && !c.userData.fx;
    /* Only for something actually casting: attaching the custom materials to
       every mesh in the scene would compile two extra programs each for
       shadows nobody asked for. */
    if(c.castShadow) ditherShadow(c, a);
  });
}

function ditherShadow(mesh, a){
  let u = mesh.userData.shadowFade;
  if(!u){
    u = mesh.userData.shadowFade = { value: 1 };
    const patch = m => {
      m.onBeforeCompile = sh => {
        sh.uniforms.uShadowFade = u;
        /* No newline needed on either: DITHER_GLSL opens with one, and GLSL
           does not care where a statement sits on its line. */
        sh.fragmentShader = 'uniform float uShadowFade;' + DITHER_GLSL +
          sh.fragmentShader.replace('void main() {',
            'void main() { if(uShadowFade <= ign(gl_FragCoord.xy)) discard;');
      };
      return m;
    };
    /* Both, because the two shadow paths use different materials: a spot or
       directional light renders depth, a point light renders distance. Giving
       only one of them the dither would fade a shadow under some lights and
       not others, which reads as a bug in the lighting rather than a limit. */
    mesh.customDepthMaterial = patch(
      new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking }));
    mesh.customDistanceMaterial = patch(new THREE.MeshDistanceMaterial());
  }
  u.value = a;
}

/* Record what each material's opacity means INDEPENDENTLY of the object's
   own, at the only moment that is knowable: right after it was built, when
   o.opacity is still exactly the number it was built from.

   Nearly every material here is constructed as `opacity: o.opacity`
   (makeMaterial), so its raw opacity says nothing about the material and
   everything about the object. Dividing that out leaves the intrinsic
   factor — 1 for an ordinary surface, 0.5 for a light's wireframe cone,
   1 for a text object's glow bitmap, which is built at full opacity with its
   alpha in the texture. applyLook then scales THAT by the object's current
   opacity, so a fade takes every layer down together and nothing gets the
   object's opacity applied to it twice.

   Doing this lazily on first touch was the original approach and was wrong:
   the animation player assigns the evaluated opacity to the object before
   applying it, so by the time a material is first touched, o.opacity is
   already the new value and the ratio is meaningless. */
function stampIntrinsicOpacity(o, node){
  const base = o.opacity == null ? 1 : o.opacity;
  for(const m of node.userData.mats || []){
    if(!m) continue;
    m.userData.bornTransparent = !!m.transparent;
    /* A zero base makes the ratio UNKNOWABLE, not zero. Every material here
       was built as `opacity: o.opacity` (makeMaterial), so at o.opacity === 0
       they are all 0 too and 0/0 says nothing about which of them were
       designed to be see-through. The only honest answer is "no intrinsic
       tint" — 1.

       Dividing by a guarded base of 1 instead gave bornOpacity = 0, and since
       applyLook multiplies by it, that zero was permanent: the object stayed
       invisible at every later opacity, while still casting a shadow and
       drawing its selection outline, because both come from geometry rather
       than from the material. A project saved while an opacity track happened
       to evaluate to 0 stores exactly that o.opacity — so it was reached just
       by saving mid-fade and reopening. */
    m.userData.bornOpacity = base > 0
      ? clamp((m.opacity == null ? 1 : m.opacity) / base, 0, 1)
      : 1;
  }
}

export function syncNode(o){
  /* Detach from whatever it is ACTUALLY parented under — content for most
     objects, but a linked child's real three.js parent is another object's
     node. content.remove() on a node that isn't one of its own children is
     silently a no-op, which used to leave a disposed, empty node dangling as
     a permanent child of its parent's node on every rebuild. */
  if(o.node){ if(o.node.parent) o.node.parent.remove(o.node); disposeNode(o.node); }
  o.node = buildNode(o);
  /* Everything the builder made goes one level down, so the outer node is
     left carrying nothing but the transform. Done here rather than in each
     of the seven builders — they all return a Group with their content as
     direct children, so one wrapper covers every type and none of them has
     to learn what a pivot is. */
  if(canPivot(o)){
    const inner = new THREE.Group();
    while(o.node.children.length) inner.add(o.node.children[0]);
    o.node.add(inner);
    o.node.userData.pivot = inner;
  }
  stampIntrinsicOpacity(o, o.node);
  syncShadowCasting(o, o.node);
  o.node.userData.objId = o.id;
  /* After the wrap, before the transform: a rebuild can change the bounding
     box (more text, a taller box), and a normalised pivot means a different
     local point once it has. Re-resolving here is what keeps "bottom centre"
     at the bottom instead of where the bottom used to be. */
  syncPivot(o);
  o.node.position.set(o.pos.x,o.pos.y,o.pos.z);
  o.node.rotation.set(o.rot.x*Math.PI/180, o.rot.y*Math.PI/180, o.rot.z*Math.PI/180);
  o.node.scale.set(o.scale.x, o.scale.y, o.scale.z);
  applyVisibility(o);
  syncParenting(o);
  /* o.node above is a BRAND NEW instance — the old one, and everything
     linked onto it, was just detached and disposed. Any of o's own children
     were still holding onto that old instance as their three.js parent, which
     is now a disconnected, disposed subtree nothing renders or walks
     matrixWorld through any more. Re-attach them to the new node the same
     way linking itself does. */
  for(const c of S.objects) if(c.parentId === o.id) syncParenting(c);
}
export function disposeNode(n){
  n.traverse(c=>{
    if(c.geometry) c.geometry.dispose();
    if(c.isLight && c.shadow && c.shadow.map){ c.shadow.map.dispose(); c.shadow.map = null; }
  });
  (n.userData.mats||[]).forEach(m=>m.dispose());
  (n.userData.texs||[]).forEach(t=>t && t.dispose());
  if(n.userData.tex) n.userData.tex.dispose();
}
/* OPACITY 0 IS INVISIBILITY, not a very faint object.

   A material at zero opacity still draws: it contributes no colour, but it
   still writes depth, so it goes on occluding whatever passes behind it. A
   video faded out was still a solid wall to anything moving through it, which
   is not what "0%" says and not what the eye expects — the object is gone, so
   it should stop blocking things, exactly as it does when its visibility is
   switched off.

   Decided in one place because THREE separate ones set node.visible — the
   builder, the transform sync and the look pass — and a rule expressed in one
   of them is a rule the other two undo. Strictly greater than zero: a
   deliberate 1% is still a visible object and still occludes, which is what
   asking for 1% means. */
export function applyVisibility(o){
  if(!o.node) return;
  const a = clamp(o.opacity == null ? 1 : o.opacity, 0, 1);
  const shown = o.type === 'null' ? o.visible !== false && markersShown : o.visible !== false && a > 0;
  /* A PARENT'S EYE HIDES THE PARENT, NOT ITS CHILDREN. A linked child's node is
     a three.js child of its parent's node, and a hidden node hides everything
     beneath it — so switching a parent off took every linked object with it,
     though each had its own eye and its own opacity. What a parent hides is what
     it is: its own content, i.e. every child of the node that is not another
     object's node. The node itself stays on, still carrying the transform the
     children ride on. Objects with nothing linked keep the plain one-flag path. */
  /* A null's node is ALWAYS on, children or not: it is a transform first, and
     its marker is the only thing about it that ever hides. */
  if(o.type === 'null' || S.objects.some(c => c.parentId === o.id)){
    o.node.visible = true;
    for(const ch of o.node.children) if(ch.userData.objId == null) ch.visible = shown;
  }else{
    o.node.visible = shown;
    for(const ch of o.node.children) if(ch.userData.objId == null) ch.visible = true;
  }
}

export function syncTransform(o){
  if(!o.node) return;
  o.node.position.set(o.pos.x,o.pos.y,o.pos.z);
  o.node.rotation.set(o.rot.x*Math.PI/180, o.rot.y*Math.PI/180, o.rot.z*Math.PI/180);
  o.node.scale.set(o.scale.x, o.scale.y, o.scale.z);
  applyVisibility(o);
}

/* ═══════════════════════════════════════════════════════════
   LINKING — one object's pos/rot/scale become relative to another's.
   ═══════════════════════════════════════════════════════════ */
/* Fixed geometry, not props: neither ever has a Place tab (see
   renderObjectInspector's fixedTransform), so neither side of a link makes
   sense for them. */
const LINK_EXCLUDED = new Set(['frame','recess']);
const D2R = Math.PI/180, R2D = 180/Math.PI;

/* Would linking child under newParentId close a loop? Walked from the
   PROPOSED parent upward — if the child itself turns up on that walk, the
   child would end up its own ancestor. Cycle-guarded against a chain that is
   already broken somehow, though normal use can never produce one. */
export function wouldCreateCycle(childId, newParentId){
  let cur = S.objects.find(o=>o.id===newParentId);
  const seen = new Set();
  while(cur){
    if(cur.id === childId) return true;
    if(seen.has(cur.id)) return false;
    seen.add(cur.id);
    cur = cur.parentId != null ? S.objects.find(o=>o.id===cur.parentId) : null;
  }
  return false;
}

/* Re-expresses one track VALUE — a position, a rotation, or a scale, exactly
   the shape a live o.pos/rot/scale or one keyframe's v already has — from
   whatever frame it was in (oldParentNode's local space, or world if that's
   null) into another (newParentNode's local space, or world if that's null).
   A single fixed conversion: it reads the two parents' CURRENT matrixWorld
   once and applies that same conversion to every value handed to it, rather
   than asking what either parent was doing at each keyframe's own time — see
   the plan's note on why that is the correct, standard "keep transform"
   behaviour rather than a shortcut. */
function rebaseTrackValue(prop, v, oldParentNode, newParentNode){
  const tmpPos = new THREE.Vector3(), tmpQuat = new THREE.Quaternion(), tmpScale = new THREE.Vector3();
  if(prop === 'pos'){
    const world = new THREE.Vector3(v.x, v.y, v.z);
    if(oldParentNode) world.applyMatrix4(oldParentNode.matrixWorld);
    if(newParentNode) world.applyMatrix4(newParentNode.matrixWorld.clone().invert());
    return { x: world.x, y: world.y, z: world.z };
  }
  if(prop === 'rot'){
    let q = new THREE.Quaternion().setFromEuler(new THREE.Euler(v.x*D2R, v.y*D2R, v.z*D2R, 'XYZ'));
    if(oldParentNode){
      oldParentNode.matrixWorld.decompose(tmpPos, tmpQuat, tmpScale);
      q = tmpQuat.clone().multiply(q);
    }
    if(newParentNode){
      newParentNode.matrixWorld.decompose(tmpPos, tmpQuat, tmpScale);
      q = tmpQuat.clone().invert().multiply(q);
    }
    const e = new THREE.Euler().setFromQuaternion(q, 'XYZ');
    return { x: e.x*R2D, y: e.y*R2D, z: e.z*R2D };
  }
  // scale — component-wise, the same no-shear assumption every other
  // transform in this app already makes (nothing here composes rotation
  // into scale either).
  const s = new THREE.Vector3(v.x, v.y, v.z);
  if(oldParentNode){ oldParentNode.matrixWorld.decompose(tmpPos, tmpQuat, tmpScale); s.multiply(tmpScale); }
  if(newParentNode){ newParentNode.matrixWorld.decompose(tmpPos, tmpQuat, tmpScale); s.divide(tmpScale); }
  return { x: s.x, y: s.y, z: s.z };
}

/* The shared math for both directions, and for deleteSel's cascade below —
   no history push, no render, so an operation touching several objects at
   once still lands as one undo step for whichever caller owns that. */
function rebaseChild(child, oldParentNode, newParentNode, newParentId){
  /* A parent's frame mixes the axes (a turned parent turns X into Y), so a track
     cannot be moved into another frame one axis at a time: any separated vector
     is combined first. The motion is unchanged; only the split is undone. */
  if(child.anim) for(const prop of ['pos','rot','scale']) combineAxes(child, prop);
  child.pos   = rebaseTrackValue('pos',   child.pos,   oldParentNode, newParentNode);
  child.rot   = rebaseTrackValue('rot',   child.rot,   oldParentNode, newParentNode);
  child.scale = rebaseTrackValue('scale', child.scale, oldParentNode, newParentNode);
  if(child.anim) for(const prop of ['pos','rot','scale'])
    if(child.anim[prop]) child.anim[prop] = child.anim[prop].map(k =>
      ({ ...k, v: rebaseTrackValue(prop, k.v, oldParentNode, newParentNode) }));
  child.parentId = newParentId;
  syncParenting(child);
  syncTransform(child);
}

/* Attaches o.node to whatever three.js node its OWN data says it belongs
   under — content, same as every unlinked object, or a live parent's node.
   Object3D.add() removes from any previous parent on its own, so this is
   safe to call unconditionally; the parent-not-yet-built case (mid bulk
   restore) just leaves it on content until the fix-up pass below reaches it. */
export function syncParenting(o){
  if(!o.node) return;
  const parent = o.parentId != null ? S.objects.find(p => p.id === o.parentId) : null;
  const target = (parent && parent.node) ? parent.node : content;
  if(o.node.parent !== target) target.add(o.node);
  /* Gaining or losing a child changes which of the two ways the parent's own
     visibility is applied (see applyVisibility). */
  if(parent) applyVisibility(parent);
}

export function linkObjects(childId, parentId){
  const child = S.objects.find(o=>o.id===childId);
  const parent = S.objects.find(o=>o.id===parentId);
  if(!child || !parent || child===parent) return false;
  if(LINK_EXCLUDED.has(child.type) || LINK_EXCLUDED.has(parent.type)) return false;
  if(wouldCreateCycle(childId, parentId)) return false;
  content.updateMatrixWorld(true);
  const oldParentNode = child.parentId != null
    ? (S.objects.find(o=>o.id===child.parentId)?.node || null) : null;
  rebaseChild(child, oldParentNode, parent.node, parentId);
  renderRails(); requestRender(); pushHistory();
  return true;
}

/**
 * PARENT TO A NEW NULL — the After Effects move, in one click: a null appears
 * exactly where the object is, and the object is linked to it. Nothing moves on
 * screen; from then on moving, turning or scaling the null carries the object.
 * One undo step for the whole of it. The object stays selected, since it is the
 * thing that was being worked on.
 *
 * @returns the new null, or null when the object cannot be linked
 */
export function parentToNewNull(childId){
  const child = S.objects.find(o=>o.id===childId);
  /* Not the camera: it IS the viewer, and a viewer riding a null is a moving
     eye nothing else in the app expects. */
  if(!child || child.parentId != null || LINK_EXCLUDED.has(child.type) || child.type === 'camera') return null;
  content.updateMatrixWorld(true);
  const wp = new THREE.Vector3();
  child.node.getWorldPosition(wp);
  const n = addObject('null');                   // pushes the step this all folds into
  /* Named for what it carries: a list of "Null 1 … Null 9" after a bulk import
     says nothing about which one moves the logo. */
  n.name = 'Null · ' + child.name;
  n.pos = { x: wp.x, y: wp.y, z: wp.z };
  syncTransform(n);
  content.updateMatrixWorld(true);
  rebaseChild(child, null, n.node, n.id);
  applyVisibility(n);
  select(child.id);
  renderRails(); requestRender(); amendHistory();
  return n;
}

export function unlinkObject(childId){
  const child = S.objects.find(o=>o.id===childId);
  if(!child || child.parentId == null) return false;
  content.updateMatrixWorld(true);
  const oldParentNode = S.objects.find(o=>o.id===child.parentId)?.node || null;
  const was = S.objects.find(o=>o.id===child.parentId);
  rebaseChild(child, oldParentNode, null, null);
  if(was) applyVisibility(was);
  renderRails(); requestRender(); pushHistory();
  return true;
}

/* ── the pivot ─────────────────────────────────────────────────────────────
   Rotation and scale happen around the node's origin, so a box leaning
   forward leans around its own middle and sinks half of itself through
   whatever it is standing on. What you almost always want is a hinge at the
   base — which is a pivot, and a pivot is an offset between the transform
   and the geometry it drives.

   Implemented as a group in between: the outer node carries position,
   rotation and scale exactly as before, and an inner group holds the
   geometry shifted by -pivot. buildGLBNode already did precisely this to
   bake in an imported model's recentring, so this generalises a pattern the
   file already had rather than introducing one.

   The consequence worth knowing: o.pos is now where the PIVOT sits, not
   where the middle of the object sits. That is what makes the gizmo land on
   the pivot for free (scene/gizmo.js positions it at o.pos), and it is why
   an object with a base pivot grows upward out of a fixed base when it is
   resized rather than expanding in both directions. */

/* A camera IS the viewer the whole projection is solved for, a light is a
   point with no extent to hinge around, and a recess and a frame have fixed
   transforms with no gizmo at all. None of the four has a meaningful pivot,
   so none of them is even wrapped — their nodes are byte-identical to what
   they were before pivots existed. */
const NO_PIVOT = ['camera', 'light', 'recess', 'frame', 'null'];
export const canPivot = o => !!o && !NO_PIVOT.includes(o.type);

/* Measured on the inner group rather than on o.node, which matters: bounds
   taken in the OUTER node's space already include the pivot shift, so using
   them to compute the pivot would feed the answer back into itself and walk
   the object across the scene one edit at a time. The inner group's own
   space is geometry space, whatever the pivot currently is. */
function pivotBounds(o){
  const inner = o.node && o.node.userData.pivot;
  if(!inner) return null;
  const b = localBounds(inner);
  return b.isEmpty() ? null : b;
}

const _pvC = new THREE.Vector3(), _pvH = new THREE.Vector3();

/**
 * The local-space point a normalised pivot refers to. -1 is one face of the
 * bounding box, +1 the opposite, 0 the middle.
 *
 * A null/absent pivot returns the origin — the geometry's own, which is what
 * every object used before this existed. Deliberately NOT the bounding-box
 * centre: a box is modelled around its middle but 3D Text is built around
 * its baseline, so treating "unset" as "centre" would shift existing text.
 */
export function pivotPoint(o, n){
  const b = n ? pivotBounds(o) : null;
  if(!b) return new THREE.Vector3(0,0,0);
  b.getCenter(_pvC); b.getSize(_pvH).multiplyScalar(.5);
  return new THREE.Vector3(_pvC.x + n.x*_pvH.x, _pvC.y + n.y*_pvH.y, _pvC.z + n.z*_pvH.z);
}

/** Push o.pivot into the node. Cheap — one vector write, no rebuild. */
export function syncPivot(o){
  const inner = o.node && o.node.userData.pivot;
  if(!inner) return;
  const p = pivotPoint(o, o.pivot);
  inner.position.set(-p.x, -p.y, -p.z);
}

/**
 * Move the pivot WITHOUT moving the object.
 *
 * The geometry sits at -p inside the group, so a local point q lands at
 * `pos + R·S·(q - p)`. Holding that still while p goes from a to b needs
 * `pos += R·S·(b - a)` — scale first, then rotate, which is the order the
 * node's own matrix composes them in. Skip the compensation and every touch
 * of the control kicks the object sideways by the distance the pivot moved.
 *
 * @param {object} o
 * @param {{x,y,z}|null} next  normalised pivot, or null for the geometry origin
 */
export function setPivot(o, next){
  if(!canPivot(o) || !o.node) return;
  const a = pivotPoint(o, o.pivot);
  const b = pivotPoint(o, next);
  const d = new THREE.Vector3().subVectors(b, a)
    .multiply(new THREE.Vector3(o.scale.x, o.scale.y, o.scale.z))
    .applyQuaternion(objQuat(o));
  o.pos.x += d.x; o.pos.y += d.y; o.pos.z += d.z;
  if(next) o.pivot = { x: next.x, y: next.y, z: next.z }; else delete o.pivot;
  syncPivot(o);
  syncTransform(o);
}

/**
 * Push colour, finish, opacity and light power into the existing materials.
 * Patch only — this NEVER rebuilds geometry, which is what makes it safe to
 * call sixty times a second from the animation player. The inspector's own
 * remat() still decides when a rebuild is genuinely needed (a text object, an
 * image, or a switch between lit and unlit swaps the material class) and
 * delegates the patch case here, so the two can never drift apart.
 *
 * Opacity goes to EVERY material, colour only to mats[0]. That asymmetry is
 * deliberate: fading a 3D Text object has to take its outline shell, its
 * extruded sides and its glow bitmap down with the fill, or the letters
 * dissolve and leave their own shadow hanging in the air. Colour is the
 * opposite — the outline and the sides have their own colours, chosen
 * separately, and o.color means the fill. mats[0] is documented as the fill
 * in buildText for exactly this reason.
 */
export function applyLook(o){
  const n = o.node;
  if(!n) return;

  /* A light's materials are its viewport marker, which never reaches an
     export — its colour and power live on the THREE.Light itself. */
  const L = n.userData.light;
  if(L){
    L.color.set(o.color);
    if(o.params && Number.isFinite(+o.params.intensity)) L.intensity = Math.max(0, +o.params.intensity);
  }

  const mats = n.userData.mats || [];
  const a = clamp(o.opacity == null ? 1 : o.opacity, 0, 1);
  for(const m of mats){
    if(!m) continue;
    /* Two things about a material have to be remembered before this touches
       it, and both are wrong to assume:

       Some are transparent by NATURE rather than by opacity — the glow/shadow
       bitmap carries its own alpha channel and must stay transparent even at
       full opacity, or it renders as an opaque black rectangle behind the
       type. So the born flag is OR'd in rather than overwritten.

       And some are already partly see-through by design: a light's wireframe
       cone is built at opacity .5 (buildLightViz). Assigning the object's
       opacity outright would push it to 1 and make the marker solid, so the
       object's opacity SCALES what the material was born with instead of
       replacing it. */
    /* stampIntrinsicOpacity (above) sets these when the node is built. The
       fallback is for a material that reached here without going through
       syncNode — there should be none, and assuming "no intrinsic tint" is
       the reading that degrades to the old behaviour rather than to a
       compounding error. */
    const born = m.userData.bornOpacity == null ? 1 : m.userData.bornOpacity;
    m.opacity = born * a;
    m.transparent = !!m.userData.bornTransparent || a < 1;
    m.needsUpdate = true;
  }

  /* Shadows come from a separate depth pass that does not consult
     material.opacity, so fading an object out left its shadow at full
     strength — a shadow cast by nothing. Most obvious the moment opacity is
     keyframed to zero, which is exactly how you would animate something
     leaving: the object goes and its shadow stays.

     Set here rather than at build time because opacity is keyframeable, so
     this has to follow the playhead. The cut is at "effectively invisible"
     rather than proportional — three renders a shadow or it does not, and
     graduating one properly means a custom depth material per object, which
     is a feature rather than a fix.

     Lights are skipped: their meshes are viewport markers that never cast.
     So is a text glow (userData.fx) — a halo casting a shadow would be a
     rectangle of shade behind the type. */
  if(!L) syncShadowCasting(o, n);
  applyVisibility(o);

  /* A model brings its own materials and has no Look tab, so its colour and
     finish are not the object's to set. Writing them here painted the file's
     first material the palette colour the layer was born with — invisibly,
     until an opacity keyframe or a fade move ran this, and then the product
     turned yellow or blue mid-animation. */
  const m0 = o.type === 'model' ? null : mats[0];
  if(m0){
    m0.color.set(o.color);
    /* MeshBasicMaterial — every unlit surface, and a light's own bulb marker
       — has neither, and inventing the properties on it would be quietly
       misleading to anyone reading the material back. */
    if(!o.unlit && !L){ m0.metalness = o.metalness; m0.roughness = o.roughness; }
  }
}

/* Re-derive the opening so the outer edge of the border keeps reaching the screen
   edge. The bevel is pre-clamped against depth here because the geometry clamps it
   too, and fitting against an unclamped value would leave a sliver of background. */
export function refitRecess(o){
  if(!o || o.type!=='recess' || o.params.autoFill === false) return;
  const P = o.params, ov = overshoot(S.screen);
  const B = recessBorders(P);
  /* ONLY BORDERS: the frame is drawn over the opening rather than around it, so
     it is no longer part of the FIT — but it is still very much part of the
     geometry, so B itself has to survive for the corner params below to carry.
     F is the fit alone. Zeroing it here rather than in the builder is the whole
     mechanism: every size derived from F is then the size it would have at
     border 0, which is exactly what "the inside follows the zero-border visual"
     asks for. On a corner it is also what stops a thicker frame making the
     notch shallower, cornerL/cornerR being the depths.
     The bevel drops out of the fit too — overlaid it rolls INWARD off the frame
     rather than flaring the mouth outward, so it no longer stands between the
     opening and the screen edge. */
  const ovl = P.borderOverlay === true;
  const F = ovl ? { top:0, right:0, bottom:0, left:0 } : B;
  const bev = Math.min(Math.max(0, P.bevel||0), Math.max(.001, P.depth)*.98);

  /* A corner screen's recess is a notch cut out of the corner rather than a box
     behind one plane, so the fit is stated in the two apertures' widths — the
     exact analogue of "the outer edge of the border reaches the screen edge",
     applied from the hinge outwards on each panel. There is deliberately no
     border at the hinge: that is what makes the two apertures read as one
     opening across the corner. See scene/geometry.js's corner builder for why
     depth stops being a parameter here. */
  if(S.cornerPanel){
    P.cornerHingeX = S.screen.w/2;
    P.cornerConvex = S.cornerPanel.convex !== false;
    /* The bevel is subtracted here exactly as the flat fit subtracts it below:
       the aperture, its fillet and the border together have to reach the panel
       edge, and the fillet pushes the lip outward by its own radius. Leaving it
       out overshot each panel by one bevel — invisible at the 0.02 m default,
       and a visible misfit the moment anyone raises it. */
    const cb = ovl ? 0 : Math.min(bev, Math.min(S.screen.w, S.cornerPanel.w, S.screen.h) * .2);
    P.cornerL = Math.max(.02, S.screen.w + ov - F.left - cb);
    P.cornerR = Math.max(.02, S.cornerPanel.w + ov - F.right - cb);
    /* All four, because the corner builder now draws four independent strips.
       X/Y are still written so a project saved here and opened in an older
       build keeps the shape it had, rather than losing its borders entirely. */
    P.cornerBorderL = B.left;
    P.cornerBorderR = B.right;
    P.cornerBorderT = B.top;
    P.cornerBorderB = B.bottom;
    P.cornerBorderX = B.left;
    P.cornerBorderY = B.top;
    P.h = Math.max(.02, S.screen.h + 2*ov - (F.top + F.bottom) - 2*cb);
    /* h is the SUM of the two borders, which sizes the opening correctly but
       says nothing about where it sits — so without this an uneven top and
       bottom shrank it at both ends instead of moving it. The flat builder has
       carried the same term as cy since it was written. */
    P.cornerOffsetY = (F.bottom - F.top) / 2;
    P.w = P.cornerL;                 // kept meaningful for anything reading it
    return;
  }
  /* Cleared, not left behind: turning corner mode off has to give back a flat
     recess, and buildGeometry dispatches on this being present. */
  if(P.cornerHingeX != null){
    delete P.cornerHingeX; delete P.cornerConvex; delete P.cornerL;
    delete P.cornerR; delete P.cornerBorderX; delete P.cornerBorderY;
  }
  P.w = Math.max(.02, S.screen.w + 2*ov - (ovl ? 0 : 2*bev) - (F.left + F.right));
  P.h = Math.max(.02, S.screen.h + 2*ov - (ovl ? 0 : 2*bev) - (F.top + F.bottom));
}

/* A frame's outer size is never a user setting — it always covers the screen,
   the same overshoot convention recessDefaults uses — so it is kept in sync
   here instead of exposed as a field. Border widths are untouched: that is
   the one thing about a frame that stays whatever the user set it to. */
export function refitFrame(o){
  if(!o || o.type!=='frame') return;
  const { w, h } = frameDefaults(S.screen);
  o.params.w = w; o.params.h = h;
}

/**
 * Proportionally rescale every object already on the scene when the
 * calibrated screen size changes — a 55" screen dialled in after everything
 * was placed against the app's much larger placeholder default shouldn't
 * leave every object hilariously oversized just because it existed before
 * the resize, the same way a brand-new object no longer would (see
 * defaultParams above). Called from ui/rail-right.js's onScreenChange with
 * the screen size from just before this particular edit.
 *
 * recess/frame are skipped — refitRecess/refitFrame already recompute them
 * to match the new screen EXACTLY, which is a stronger guarantee than a
 * proportional nudge and must not be second-guessed by one.
 *
 * Position always scales with mRatio (the same screen's-shorter-side basis
 * recessDefaults/frameDefaults use) for every other type, EXCEPT light:
 * its own creation formula (addObject, below) places it off S.screen.h
 * specifically, not the shorter side, so an existing light has to move by
 * hRatio to stay consistent with that — and since illuminance falls off
 * with distance squared, intensity has to scale by hRatio² to keep the
 * same apparent brightness at that new distance, not by hRatio itself.
 * Camera and model get repositioned but not resized: a camera has no size
 * to speak of, and a model has no app-assigned reference size the way
 * capH/w/baseSize are — see defaultParams' own comment.
 */
/* `list` defaults to the whole scene, which is every existing caller. Import
   passes a subset: objects arriving from another project have to be brought
   from THAT project's screen to this one, and the objects already here must not
   move because something was pasted next to them. */
export function rescaleForScreen(oldScreen, newScreen, list = S.objects){
  const om = Math.min(oldScreen.w, oldScreen.h), nm = Math.min(newScreen.w, newScreen.h);
  const mRatio = om > 1e-6 ? nm / om : 1;
  const hRatio = oldScreen.h > 1e-6 ? newScreen.h / oldScreen.h : 1;
  if(Math.abs(mRatio - 1) < 1e-6 && Math.abs(hRatio - 1) < 1e-6) return;

  list.forEach(o=>{
    if(o.type === 'recess' || o.type === 'frame') return;
    /* A linked child already inherits this rescale transitively through its
       parent's own (also being rescaled) node transform — rescaling it again
       here would double-apply the ratio to its translation. */
    if(o.parentId != null && S.objects.some(p=>p.id===o.parentId)) return;
    const r = o.type === 'light' ? hRatio : mRatio;
    o.pos.x *= r; o.pos.y *= r; o.pos.z *= r;
    let resized = true;
    if(o.type === 'text') o.params.capH *= mRatio;
    else if(o.type === 'textarea'){ o.params.w *= mRatio; o.params.baseSize *= mRatio; o.params.padding *= mRatio; }
    else if(o.type === 'image') o.params.w *= mRatio;
    /* Depth and bevel scale too, or a panel keeps its old thickness on a
       screen twice the size and reads as a much thinner sheet. */
    else if(o.type === 'video'){ o.params.w *= mRatio; o.params.depth *= mRatio; o.params.bevel *= mRatio; }
    else if(o.type === 'light') o.params.intensity *= hRatio * hRatio;
    else resized = false;   // camera, model — position only
    syncTransform(o);
    if(resized) syncNode(o);
  });
}

/* How far in front of the screen a new object starts. A generic prop pops
   well out toward the viewer by default, which is the point of most objects
   here — but text reads as copy sitting ON the surface, not a shape floating
   in the room, so it gets its own much shallower defaults, and an image sits
   closer still (a photo cutout, not a deep prop). Text Area in particular is
   meant to sit flush with the surface (parallel to it, not tilted toward the
   eye — that's what faceCamera is for, and it stays off here) rather than
   pop out at all; a hair off zero only to keep it from z-fighting whatever
   else sits exactly on the screen plane, like a recess border. */
function spawnZ(type){
  if(type === 'recess') return 0;
  if(type === 'frame') return .01;
  if(type === 'textarea') return .02;
  if(type === 'text') return Math.max(.1, S.screen.h*0.04);
  if(type === 'image' || type === 'video') return Math.max(.05, S.screen.h*0.08);
  return Math.max(.4, S.screen.h*0.28);
}

/* Every type whose DEF has a size (or, for light, an intensity) that reads
   fine at the app's own placeholder screen but wrong at a real calibrated
   one far from that size gets a screen-relative default here instead of a
   flat copy of DEF[type] — same idea recessDefaults/frameDefaults already
   established, just extended past recess/frame to cover the rest. Camera
   and model aren't listed: camera's own eye distance is already computed
   from S.screen directly below, and a model has no size the app assigns at
   all — its native GLB scale is the only "default" there is. */
function defaultParams(type, screen){
  switch(type){
    case 'recess': return recessDefaults(screen);
    case 'frame': return frameDefaults(screen);
    case 'text': return textDefaults(screen);
    case 'textarea': return textareaDefaults(screen);
    case 'image': return imageDefaults(screen);
    case 'video': return videoDefaults(screen);
    case 'light': return lightDefaults(screen);
    case 'shape': return shapeDefaults(screen);
    default: return JSON.parse(JSON.stringify(DEF[type]));
  }
}

export function addObject(type, extra){
  const o = {
    id: S.nextId++,
    type,
    name: NICE[type] + ' ' + (S.objects.filter(x=>x.type===type).length+1),
    pos:{x:0,y:0,z: spawnZ(type)}, rot:{x:0,y:0,z:0},
    scale:{x:1,y:1,z:1}, lockScale:true,
    color: type==='recess' ? '#D5D8DC' : type==='frame' ? '#000000' : PALETTE[(S.nextId-2)%PALETTE.length],
    metalness:0, roughness:.42, opacity:1,
    /* Text Area and Image are WYSIWYG — the colours/pixels picked should render
       exactly as chosen, not shaded by whatever's lighting the scene. A frame
       is a physical black bezel, not a lit prop, and stays that way — see the
       inspector, which skips its Surface panel entirely rather than exposing
       knobs that would only ever be put back to these same values. Casting a
       shadow is unaffected by any of this — see makeMaterial. */
    unlit: type==='textarea' || type==='frame' || type==='image',
    // See makeMaterial's applyBlendMode — 'normal' is THREE's own default
    // and needs nothing set, so every type but image starts here and stays
    // there; only image surfaces a picker for the rest (see rail-right.js).
    blend: 'normal',
    // A frame is fixed geometry, not a lit prop. A Text Area is a caption
    // flush with the surface — it does not cast a shadow by default either,
    // though the option still exists (see the Surface panel's checkbox).
    shadow: type!=='frame' && type!=='textarea',
    visible:true, faceCamera:false, faceRoll:0, sideOn:false, sideColor:'#101010', sideRough:null,
    params: defaultParams(type, S.screen),
    node:null
  };
  /* A recess is fitted to the screen the moment it exists, not only when the
     screen later changes. That gap is how a corner project ended up with a
     FLAT recess: the corner is marked in Calibrate, the recess is seeded on
     the first solve (see seedRecess), and defaultParams only knows how to
     build the flat box — so it was a slab behind the first panel, which the
     second panel sees edge-on and renders as nothing but background. The
     symptom was "the right side is missing", and it was never the geometry. */
  if(type==='recess'){ o.roughness = .72; refitRecess(o); }
  // a texture (an image, or a Text Area's rendered canvas) must not carry a palette tint
  if(type==='image' || type==='textarea' || type==='video') o.color = '#ffffff';
  if(type==='null'){ o.color = '#E8A33C'; o.shadow = false; o.pos.z = 0; }
  if(type==='camera'){
    // straight in front of the screen, square to it — the default viewpoint
    o.pos = {x:0, y:0, z: Math.hypot(S.screen.w, S.screen.h)*1.5};
    o.rot = {x:0, y:0, z:0};
    o.color = '#5BA9D6';
    o.shadow = false;
  }
  if(type==='light'){
    // Dead centre, pointing straight at the recess — the obvious starting
    // point now that a fresh light defaults to an omnidirectional point light.
    o.pos = {x: 0, y: S.screen.h*.5, z: Math.max(.3, S.screen.h*.35)};
    o.rot = aimEuler(new THREE.Vector3(o.pos.x,o.pos.y,o.pos.z), new THREE.Vector3(0,0,0));
    o.color = '#FFF1DC';
  }
  if(type==='text'){
    // Never bigger than the recess by default — at least 30% smaller, so a
    // freshly added string reads as sized to sit inside it instead of
    // needing an immediate manual shrink. textDefaults() above already
    // sizes capH off the screen itself, but the ACTUAL recess can differ
    // from a fresh recessDefaults() if it's been manually resized — this
    // is the extra safety net for that case, not the primary sizing
    // mechanism any more. Divided across the default text's own line count
    // so a multi-line string doesn't overflow the recess vertically even
    // though capH alone stays within the 70% bound. Only ever shrinks —
    // never grows past textDefaults()'s own value — and only applies to a
    // brand-new object here, not a later manual resize.
    const recess = S.objects.find(x=>x.type==='recess');
    if(recess){
      const lines = Math.max(1, o.params.text.split('\n').length);
      o.params.capH = Math.min(o.params.capH, (recess.params.h*0.7)/lines);
    }
  }
  if(extra) Object.assign(o.params, extra);
  S.objects.push(o);
  syncNode(o);
  select(o.id);
  renderRails();
  requestRender();
  pushHistory();
  return o;
}
export function getSel(){ return S.objects.find(o=>o.id===S.selId) || null; }

export function select(id){
  S.selId = id;
  S.eyeSelected = false;   // the three are never both active — see their comments in store.js
  S.arraySelected = false;
  setHover(null);
  renderRails();
  /* SAY SO, rather than only telling the rails.
     The timeline marks the selected object's rows and decides that at build
     time, and renderRails() is renderLeft + renderRight — it has never touched
     the dock. So selecting something in the Scene list moved the highlight
     there and left the timeline marking whatever was selected when it was last
     built, which is how the two came to disagree.

     'sel:changed' is the event state/bus.js has documented all along and
     ui/timeline-view.js has subscribed to all along; nothing had ever emitted
     it, so that subscription was dead code. This is the emitter it was waiting
     for — the bus is how the scene layer talks to the UI without importing it
     (see the note at the top of bus.js). */
  emit('sel:changed', id);
  updateHint(); syncGizmoBar();
  requestRender();
}
/**
 * Move one object to sit beside another in the layer order.
 *
 * The order is the Scene list's, which shows S.objects REVERSED — newest at
 * the top, the way every layer list works. So the move is done in display
 * space and reversed back, rather than with index arithmetic that has to
 * subtract from the length and gets the ends wrong.
 *
 * It changes nothing about what is drawn. content.add() runs once per object
 * in syncNode, so the three.js group's child order is creation order and is
 * not touched here — depth in this app comes from Z position, not from list
 * order. This is organisation: which row sits where, in both lists.
 *
 * @param dragId  the object being moved
 * @param dropId  the object it is being dropped onto
 * @param after   below that row rather than above it, in Scene-list terms
 * @returns true if the order actually changed
 */
export function moveObject(dragId, dropId, after){
  if(dragId === dropId) return false;
  const view = S.objects.slice().reverse();              // as the Scene list shows them
  const from = view.findIndex(o => o.id === dragId);
  if(from < 0) return false;
  const [moved] = view.splice(from, 1);
  let to = view.findIndex(o => o.id === dropId);
  if(to < 0) return false;                               // dropped on nothing real
  if(after) to++;
  view.splice(to, 0, moved);
  const next = view.reverse();
  if(next.every((o, i) => o === S.objects[i])) return false;
  S.objects = next;
  /* The dock lists the same objects in the same order, and 'scene:changed' is
     the event state/bus.js documents for exactly this — ui/timeline-view.js
     has subscribed to it since it was written and nothing had ever emitted it.
     Same story as 'sel:changed' in select() above. */
  emit('scene:changed');
  renderRails();
  requestRender();
  pushHistory();
  return true;
}

export function deleteSel(){
  const o = getSel(); if(!o) return;
  /* A child left pointing at a dead id is worse than one quietly set free —
     rebase every direct child back to world first, exactly what a manual
     Unlink does, so deleting the thing they were riding on doesn't also
     teleport them. Uses the private helper directly (not unlinkObject) so
     this cascade doesn't push its own history entries — the delete below is
     the one undo step this whole action should be. */
  content.updateMatrixWorld(true);
  S.objects.filter(x=>x.parentId===o.id).forEach(c=>rebaseChild(c, o.node, null, null));
  if(o.node.parent) o.node.parent.remove(o.node);
  disposeNode(o.node);
  S.objects = S.objects.filter(x=>x.id!==o.id);
  S.selId = null;
  /* Sets S.selId itself rather than going through select(), so it has to
     broadcast for itself — and the dock needs telling twice over here: the
     selection moved AND the rows belonging to a deleted object have to go. */
  emit('sel:changed', null);
  renderRails(); requestRender(); pushHistory();
}
export function duplicateSel(){
  const o = getSel(); if(!o) return;
  // Only one camera object ever makes sense — it IS the viewer, and
  // eyeVec()/cameraObj() (scene/helpers.js) only ever look at the first
  // one in S.objects, so a second would just sit there doing nothing
  // while looking like it should do something.
  if(o.type==='camera'){ toast('Only one camera is allowed — it is the viewer, not a prop.', true); return; }
  const c = JSON.parse(JSON.stringify({...o, node:undefined}));
  c.id = S.nextId++;
  c.name = o.name + ' copy';
  // a frame's (and a recess's) position is fixed — nudging the copy sideways would break that
  if(o.type!=='frame' && o.type!=='recess') c.pos.x += Math.max(.15, S.screen.w*0.04);
  c.node = null;
  S.objects.push(c); syncNode(c); select(c.id); pushHistory();
}


$('#fileImg').onchange = e=>{
  const f = e.target.files[0]; if(!f) return;
  const rd = new FileReader();
  rd.onload = ()=>{ const o = addObject('image',{src:rd.result}); o.name = f.name.replace(/\.[^.]+$/,''); renderRails(); };
  rd.readAsDataURL(f);
  e.target.value='';
};

/**
 * Everything a model layer needs from a .glb: the bytes as a data URL, where
 * its own centre is, and the scale that makes it a sensible size here.
 *
 * Split out of importGLB so that REPLACING the file on an existing model runs
 * the identical parse and auto-fit — the same split prepareVideo makes, and
 * for the same reason: a second, slightly different copy would drift.
 *
 * Rejects with a sentence worth showing; the caller turns it into a toast.
 */
export function parseModelFile(file){
  return new Promise((resolve, reject)=>{
    const rd = new FileReader();
    rd.onerror = ()=>reject(new Error('That file could not be read.'));
    rd.onload = ()=>{
      try{
        const arr = rd.result;                       // ArrayBuffer
        const { json, bin } = parseGLB(arr);
        const buffers = resolveBuffers(json, bin);
        const meshes = extractMeshes(json, buffers);
        if(!meshes.length) throw new Error('That model has no triangle meshes to import.');
        const b = boundsOf(meshes);
        // auto-fit: recentre on the origin and scale so the largest side is a
        // sensible fraction of the screen height, so props arrive usable whatever
        // units they were exported in (metres, centimetres, inches...).
        const maxSide = Math.max(b.size[0], b.size[1], b.size[2]) || 1;
        const target  = Math.max(0.2, S.screen.h * 0.45);
        const fit = target / maxSide;
        const tris = meshes.reduce((s,m)=>s+m.tris,0);
        // data URL so history-vaulting and project-save reuse the existing path
        let bin2 = '';
        const u8 = new Uint8Array(arr);
        for(let i=0;i<u8.length;i++) bin2 += String.fromCharCode(u8[i]);
        resolve({ src: 'data:model/gltf-binary;base64,' + btoa(bin2),
                  center: b.center, fit, tris });
      }catch(err){ reject(err); }
    };
    rd.readAsArrayBuffer(file);
  });
}

/** A basic shape — cube, sphere, cylinder… — sized for the current screen and
 *  named for what it is ("Sphere 2") rather than the type ("3D Shape 2"), the
 *  same courtesy importGLB pays a model by naming it after its file. */
export function addShape(kind){
  const o = addObject('shape', shapeDefaults(S.screen, kind));
  const label = SHAPES[o.params.shape].label;
  o.name = label + ' ' + S.objects.filter(x=>x.type==='shape' && x.params.shape===o.params.shape).length;
  renderRails();
  return o;
}

export async function importGLB(file){
  try{
    const { src, center, fit, tris } = await parseModelFile(file);
    const o = addObject('model', { src, center, fit, tris });
    o.name = file.name.replace(/\.[^.]+$/,'');
    renderRails();
    toast('Imported ' + o.name + '  ' + tris.toLocaleString() + ' triangles. Drag it into place.');
  }catch(err){ toast('Could not import that model: ' + err.message, true); }
}
$('#fileGlb').onchange = e=>{ if(e.target.files[0]) importGLB(e.target.files[0]); e.target.value=''; };

